/**
 * Admin dashboard tab router — code-split so each tab is fetched only
 * when the admin actually opens it. Cut the initial admin JS bundle by
 * ~55% (57 tab components → 1 eagerly-imported Overview + 56 lazy).
 *
 * The Overview tab stays eager because it's the landing tab after login;
 * lazy-loading it would make the FIRST paint slower, which is the
 * opposite of what we want. Every other tab is `React.lazy`.
 *
 * Suspense fallback renders a small spinner in the content area so the
 * admin sees SOMETHING immediately when they click a new tab, instead
 * of a blank white pane during the ~150ms chunk fetch.
 */
import { Suspense, lazy } from "react";
import { Loader2 } from "lucide-react";
import AdminOverviewTab from "@/components/admin/AdminOverviewTab";

// Every non-overview tab as a code-split chunk. Grouped by concern so a
// reader can scan the imports and know what's in each admin area.
const StaffProfileTab            = lazy(() => import("@/components/prescriber/StaffProfileTab"));
const StaffChatTab               = lazy(() => import("@/components/StaffChatTab"));
const ComplianceReportsTab       = lazy(() => import("@/components/admin/ComplianceReportsTab"));
const SettingsTab                = lazy(() => import("@/components/admin/SettingsTab"));
const SystemOpsTab               = lazy(() => import("@/components/admin/SystemOpsTab"));
const SeoAndCrawlersTab          = lazy(() => import("@/components/admin/SeoAndCrawlersTab"));
const AiCitationsTab             = lazy(() => import("@/components/admin/AiCitationsTab"));
const CompetitorDirectoryTab     = lazy(() => import("@/components/admin/CompetitorDirectoryTab"));
const IdVerificationSettingsTab  = lazy(() => import("@/components/admin/IdVerificationSettingsTab"));
const MedicationsManagementTab   = lazy(() => import("@/components/admin/MedicationsManagementTab"));
const BAAsTab                    = lazy(() => import("@/components/admin/BAAsTab"));
const TestimonialsTab            = lazy(() => import("@/components/admin/TestimonialsTab"));
const RxTemplatesTab             = lazy(() => import("@/components/admin/RxTemplatesTab"));
const DispenseTemplatesTab       = lazy(() => import("@/components/admin/DispenseTemplatesTab"));
const EmailTemplatesTab          = lazy(() => import("@/components/admin/EmailTemplatesTab"));
const SmsTemplatesTab            = lazy(() => import("@/components/admin/SmsTemplatesTab"));
const CannedResponsesTab         = lazy(() => import("@/components/admin/CannedResponsesTab"));
const ChatBotTab                 = lazy(() => import("@/components/admin/ChatBotTab"));
const DeliveryExceptionsAdminTab = lazy(() => import("@/components/admin/DeliveryExceptionsAdminTab"));
const LoyaltyCouponsTab          = lazy(() => import("@/components/admin/LoyaltyCouponsTab"));
const MarketingCouponsTab        = lazy(() => import("@/components/admin/MarketingCouponsTab"));
const TrackingQueueTab           = lazy(() => import("@/components/admin/TrackingQueueTab"));
const HealthTab                  = lazy(() => import("@/components/admin/HealthTab"));
const ErrorReportsTab            = lazy(() => import("@/components/admin/ErrorReportsTab"));
const AuditLogsTab               = lazy(() => import("@/components/admin/AuditLogsTab"));
const SuspiciousActivityTab      = lazy(() => import("@/components/admin/SuspiciousActivityTab"));
const PharmacyInquiriesTab       = lazy(() => import("@/components/admin/PharmacyInquiriesTab"));
const UsersTab                   = lazy(() => import("@/components/admin/UsersTab"));
const OrdersTab                  = lazy(() => import("@/components/admin/OrdersTab"));
const PendingRefundsTab          = lazy(() => import("@/components/admin/PendingRefundsTab"));
const ErxRecordsTab              = lazy(() => import("@/components/admin/ErxRecordsTab"));
const AnalyticsTab               = lazy(() => import("@/components/admin/AnalyticsTab"));
const BannersManagementTab       = lazy(() => import("@/components/admin/BannersManagementTab"));
const StepathonAdminTab          = lazy(() => import("@/components/admin/StepathonAdminTab"));
const YouTubeManagementTab       = lazy(() => import("@/components/admin/YouTubeManagementTab"));
const PharmaciesManagementTab    = lazy(() => import("@/components/admin/PharmaciesManagementTab"));
const LifeFileConfigTab          = lazy(() => import("@/components/admin/LifeFileConfigTab"));
const LifeFileKPIsCard           = lazy(() => import("@/components/admin/LifeFileKPIsCard"));
const ShippingStudyPageEditor    = lazy(() => import("@/components/admin/ShippingStudyPageEditor"));
const TrainingStatusTab          = lazy(() => import("@/components/admin/TrainingStatusTab"));
const SandboxAdminTab            = lazy(() => import("@/components/admin/SandboxAdminTab"));
const ManualTrackingUploadTab    = lazy(() => import("@/components/admin/ManualTrackingUploadTab"));
const ManualSubscriptionsTab     = lazy(() => import("@/components/admin/ManualSubscriptionsTab"));
const FactOrMythManagementTab    = lazy(() => import("@/components/admin/FactOrMythManagementTab"));
const FAQManagementTab           = lazy(() => import("@/components/admin/FAQManagementTab"));
const UnlockRequestsTab          = lazy(() => import("@/components/admin/UnlockRequestsTab"));
const SevereSymptomsTab          = lazy(() => import("@/components/admin/SevereSymptomsTab"));
const ReviewsManagementTab       = lazy(() => import("@/components/admin/ReviewsManagementTab"));
const PricingManagementTab       = lazy(() => import("@/components/admin/PricingManagementTab"));
const TierBulletsTab             = lazy(() => import("@/components/admin/TierBulletsTab"));
const ComplianceTab              = lazy(() => import("@/components/admin/ComplianceTab"));
const TimeClockTab               = lazy(() => import("@/components/admin/TimeClockTab"));
const StaffTrainingManual        = lazy(() => import("@/components/StaffTrainingManual"));
const CoaManagementTab           = lazy(() => import("@/components/admin/CoaManagementTab"));
const StripePaymentsTab          = lazy(() => import("@/components/admin/StripePaymentsTab"));
const SubscriptionsTab           = lazy(() => import("@/components/admin/SubscriptionsTab"));
const PaymentProcessorTab        = lazy(() => import("@/components/admin/PaymentProcessorTab"));

const TAB_REGISTRY = {
  overview: (ctx) => <AdminOverviewTab {...ctx} />,
  myprofile: () => <StaffProfileTab />,
  analytics: () => <AnalyticsTab />,
  reports: () => <ComplianceReportsTab />,
  users: (ctx) => (
    <UsersTab
      users={ctx.users}
      roleFilter={ctx.roleFilter}
      setRoleFilter={ctx.setRoleFilter}
      onEdit={ctx.openEditUser}
      onToggleActive={ctx.toggleActive}
      onView={ctx.openPatientDetail}
      onReload={ctx.loadAll}
    />
  ),
  orders: (ctx) => <OrdersTab orders={ctx.orders} />,
  medications: () => <MedicationsManagementTab />,
  rxtemplates: () => <RxTemplatesTab />,
  dispensetemplates: () => <DispenseTemplatesTab />,
  erx: (ctx) => <ErxRecordsTab erxRecords={ctx.erxRecords} />,
  audit: () => <AuditLogsTab />,
  suspicious: () => <SuspiciousActivityTab />,
  "pharmacy-inquiries": () => <PharmacyInquiriesTab />,
  chat: () => <StaffChatTab />,
  "delivery-exceptions": () => <DeliveryExceptionsAdminTab />,
  canned: () => <CannedResponsesTab />,
  "chat-bot": () => <ChatBotTab />,
  banners: () => <BannersManagementTab />,
  stepathon: () => <StepathonAdminTab />,
  factmyth: () => <FactOrMythManagementTab />,
  faq: () => <FAQManagementTab />,
  "unlock-requests": () => <UnlockRequestsTab />,
  "severe-symptoms": (ctx) => (
    <SevereSymptomsTab notifPerm={ctx.notifPerm} onEnableNotifs={ctx.requestNotifPermission} />
  ),
  youtube: () => <YouTubeManagementTab />,
  pharmacy: () => <PharmaciesManagementTab />,
  "lifefile-config": () => <LifeFileConfigTab />,
  "lifefile-kpis": () => <LifeFileKPIsCard />,
  "payment-processor": () => <PaymentProcessorTab />,
  coas: () => <CoaManagementTab />,
  "shipping-study-page": () => <ShippingStudyPageEditor />,
  reviews: () => <ReviewsManagementTab />,
  baa: () => <BAAsTab />,
  "baa-register": () => <BAAsTab />,   // legacy alias for old bookmarks + widgets
  "pharmacy-docs": () => <BAAsTab />,  // legacy alias — Legacy Docs was retired; land users on the closest match
  emails: () => <EmailTemplatesTab />,
  sms: () => <SmsTemplatesTab />,
  loyalty: () => <LoyaltyCouponsTab />,
  marketing: () => <MarketingCouponsTab />,
  pricing: () => <PricingManagementTab />,
  "tier-bullets": () => <TierBulletsTab />,
  testimonials: () => <TestimonialsTab />,
  tracking: () => <TrackingQueueTab />,
  "stripe-payments": () => <StripePaymentsTab />,
  "subscriptions": () => <SubscriptionsTab />,
  refunds: () => <PendingRefundsTab />,
  health: () => <HealthTab />,
  errors: () => <ErrorReportsTab />,
  compliance: () => <ComplianceTab />,
  timeclock: () => <TimeClockTab />,
  "training-status": () => <TrainingStatusTab />,
  "sandbox-training": () => <SandboxAdminTab />,
  "manual-tracking": () => <ManualTrackingUploadTab />,
  "manual-subscriptions": () => <ManualSubscriptionsTab />,
  training: () => <StaffTrainingManual />,
  "system-ops": () => <SystemOpsTab />,
  "seo-and-crawlers": () => <SeoAndCrawlersTab />,
  "ai-citations": () => <AiCitationsTab />,
  "competitor-directory": () => <CompetitorDirectoryTab />,
  "id-verification-settings": () => <IdVerificationSettingsTab />,
  settings: () => <SettingsTab />,
};

function TabLoadingFallback() {
  return (
    <div className="flex items-center justify-center py-16" data-testid="admin-tab-loading">
      <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
      <span className="ml-3 text-sm text-slate-500">Loading tab…</span>
    </div>
  );
}

export default function AdminTabRouter({ activeTab, ctx }) {
  const factory = TAB_REGISTRY[activeTab] || TAB_REGISTRY.settings;
  return (
    <Suspense fallback={<TabLoadingFallback />}>
      {factory(ctx)}
    </Suspense>
  );
}
